/**
 * Hook für die Push-Notification-Anbindung.
 *
 * Liefert State + Aktionen, die der Toggle-Button in der UI nutzen kann:
 *  - supported  : Browser kann Push (ServiceWorker + PushManager + Notification API)?
 *  - permission : 'default' | 'granted' | 'denied'
 *  - subscribed : Aktuell beim Server registriert?
 *  - busy       : gerade eine Operation in Arbeit?
 *  - enable()   : Permission anfragen + Subscription erzeugen + an Server senden
 *  - disable()  : Browser-seitig unsubscriben + Server informieren
 *  - error      : letzte Fehlermeldung (oder null)
 */

import { useCallback, useEffect, useState } from 'react';
import { pushApi } from '../lib/push';
import { useAuth } from '../store/auth';
import { ApiError } from '../lib/api';

const SUPPORTED =
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

function urlBase64ToUint8Array(base64: string): Uint8Array {
  // VAPID-Public-Keys sind base64url. Browser brauchen Uint8Array.
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Gesamtprüfung 11.09.2026: hat die Person Push auf diesem Gerät bewusst
 * ausgeschaltet, nach dem nächsten Login NICHT automatisch wieder anmelden.
 */
const PUSH_OPT_OUT_PREFIX = 'shiftsync.pushOptOut.';
function optOutKey(userId: string | undefined): string {
  return `${PUSH_OPT_OUT_PREFIX}${userId ?? 'unknown'}`;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!SUPPORTED) return null;
  // ready wartet, bis der SW aktiv ist (kann beim ersten Page-Load kurz dauern).
  return navigator.serviceWorker.ready;
}

export interface PushHookState {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
  busy: boolean;
  error: string | null;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

export function usePush(): PushHookState {
  const token = useAuth((s) => s.token);
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    SUPPORTED ? Notification.permission : 'unsupported',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Beim Mount: aktuellen Subscription-Stand prüfen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!SUPPORTED) return;
      const reg = await getRegistration();
      if (!reg) return;
      const existing = await reg.pushManager.getSubscription();
      if (!cancelled) setSubscribed(Boolean(existing));
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const enable = useCallback(async () => {
    if (!SUPPORTED || !token) return;
    setBusy(true);
    setError(null);
    try {
      // 1. Permission anfragen
      let perm = Notification.permission;
      if (perm === 'default') {
        perm = await Notification.requestPermission();
        setPermission(perm);
      }
      if (perm !== 'granted') {
        setError('Berechtigung für Benachrichtigungen wurde abgelehnt.');
        return;
      }

      // 2. VAPID-Public-Key vom Server
      const publicKey = await pushApi.vapidPublicKey(token);

      // 3. Im Browser subscriben
      const reg = await getRegistration();
      if (!reg) throw new Error('Service Worker nicht aktiv');
      // Cast auf BufferSource: TS 5.7 typisiert Uint8Array<ArrayBufferLike>
      // strenger; Browser-Push-API akzeptiert das aber problemlos.
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      // 4. Beim Server registrieren
      await pushApi.subscribe(token, sub.toJSON(), inferDeviceName());
      setSubscribed(true);
      try {
        localStorage.removeItem(optOutKey(useAuth.getState().user?.id));
      } catch {
        // egal
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setError('Push ist serverseitig nicht konfiguriert.');
      } else {
        setError(err instanceof Error ? err.message : 'Fehler beim Aktivieren');
      }
    } finally {
      setBusy(false);
    }
  }, [token]);

  const disable = useCallback(async () => {
    if (!SUPPORTED || !token) return;
    setBusy(true);
    setError(null);
    try {
      const reg = await getRegistration();
      if (!reg) return;
      const existing = await reg.pushManager.getSubscription();
      if (existing) {
        const endpoint = existing.endpoint;
        await existing.unsubscribe();
        await pushApi.unsubscribe(token, endpoint);
      }
      setSubscribed(false);
      try {
        localStorage.setItem(optOutKey(useAuth.getState().user?.id), '1');
      } catch {
        // egal
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Abmelden');
    } finally {
      setBusy(false);
    }
  }, [token]);

  return {
    supported: SUPPORTED,
    permission,
    subscribed,
    busy,
    error,
    enable,
    disable,
  };
}

/** Versucht, einen Geräte-Namen zu erraten (UA-basiert). Reine Anzeige-Info. */
function inferDeviceName(): string {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Browser';
}

/**
 * Gesamtprüfung 11.09.2026: nach dem Login die Push-Anmeldung dieses Geräts
 * der angemeldeten Person zuordnen — bestehende Browser-Anmeldung erneut an
 * den Server melden (der Server entfernt denselben Endpunkt bei allen anderen
 * Konten), bzw. neu anmelden, wenn die Berechtigung schon erteilt ist und die
 * Person Push nicht bewusst ausgeschaltet hat. Still, ohne Fehlermeldung.
 */
export async function ensurePushSubscription(token: string, userId: string): Promise<void> {
  if (!SUPPORTED || Notification.permission !== 'granted') return;
  try {
    // Opt-out gilt je Person (geteiltes Gerät) und überlebt das Abmelden.
    if (localStorage.getItem(optOutKey(userId)) === '1') return;
  } catch {
    // localStorage nicht verfügbar — trotzdem versuchen.
  }
  try {
    const reg = await getRegistration();
    if (!reg) return;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const publicKey = await pushApi.vapidPublicKey(token);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });
    }
    await pushApi.subscribe(token, sub.toJSON(), inferDeviceName());
  } catch {
    // Push nicht konfiguriert / nicht möglich — kein Fehler für die Person.
  }
}
